import moment from 'moment-timezone';

import type { I18nService } from '@core/i18n-legacy';

const round = (num: number) => Math.round((num + Number.EPSILON) * 100) / 100;

export function getMinutes(ms: number) {
  return round(moment.duration(ms, 'ms').asMinutes());
}

export function getHours(ms: number) {
  return round(moment.duration(ms, 'ms').asHours());
}

export function getDays(ms: number) {
  return round(moment.duration(ms, 'ms').asDays());
}

type RangeName = 'empty' | 'seconds' | 'minutes-short' | 'minutes-long' | 'hours' | 'days';

export const durationFormatters: Record<
  RangeName,
  {
    min: number;
    max: number;
    formatter: (seconds: number, i18n: I18nService) => string;
  }
> = {
  empty: { min: Number.NEGATIVE_INFINITY, max: 0, formatter: () => `-` },
  seconds: {
    min: 1,
    max: 60,
    formatter: (seconds, i18n) => i18n.translate('app.common.x_seconds', { count: seconds }),
  },
  'minutes-short': {
    min: 61,
    max: 599,
    formatter: (seconds, i18n) => {
      const duration = moment.duration(seconds, 'seconds');
      const durationMinutes = duration.minutes();
      const durationSeconds = duration.subtract(duration.minutes(), 'm').seconds();
      if (durationSeconds > 0) {
        return `${durationMinutes}m ${durationSeconds}s`;
      }

      return i18n.translate('app.common.x_minutes', {
        count: Math.round(getMinutes(seconds * 1000)),
      });
    },
  },
  'minutes-long': {
    min: 600,
    max: 3599,
    formatter: (seconds, i18n) =>
      i18n.translate('app.common.x_minutes', {
        count: Math.round(getMinutes(seconds * 1000)),
      }),
  },
  hours: {
    min: 3600,
    max: 60 * 60 * 23.5,
    formatter: (seconds, i18n) =>
      i18n.translate('app.common.x_hours_plural', {
        count: Math.round(getHours(seconds * 1000)),
      }),
  },
  days: {
    min: 60 * 60 * 23.5,
    max: Number.POSITIVE_INFINITY,
    formatter: (seconds, i18n) =>
      i18n.translate('app.common.x_days_plural', {
        count: Math.round(getDays(seconds * 1000)),
      }),
  },
};

const formatDurationRules: Array<{
  range: [min: number, max: number];
  format: (seconds: number, i18n: I18nService) => string;
}> = Object.values(durationFormatters).map(({ min, max, formatter }) => ({
  range: [min, max],
  format: formatter,
}));

export const formatDuration = (seconds: number, i18n: I18nService): string => {
  const x = formatDurationRules.find(
    ({ range: [min, max], format }) => min <= seconds && max >= seconds,
  );

  if (x) return x.format(seconds, i18n);

  return `${seconds}s`;
};
